import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid2,
  Stack,
  Toolbar,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useState, useCallback, useRef } from 'react'
import { HorizontalLinearStepper } from '@common/components/Stepper'
import AddFilesStep from '../AddFilesStep'
import CheckStep, { CheckStepHandle } from '../CheckStep'
import UploadStep from '../UploadStep'
import { ArrowBackRounded } from '@mui/icons-material'
import { useStepper } from '@common/context/StepperContext'
import { AdherenceCheckUpsertPayload, UploadDialogProps } from '@features/subject/subject.types'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import PHIRedactionStep from '../PHIRedactionStep'
import { createUppy, resetUppy } from '@common/utils/createUppy'

export default function UploadDialog({
  timepointName,
  subjectPseudoId,
  containerParam,
  studyId,
  subjectId,
  timepointSubmissionId,
  onSave,
  onClose,
}: UploadDialogProps) {
  const theme = useTheme()
  const { steps, activeStep, setSteps, setActiveStep } = useStepper()
  const [uppy] = useState(() => createUppy())
  const [mode, setMode] = useState<'STEPPER' | 'UPLOAD'>('STEPPER')
  const [continueAllowed, setContinueAllowed] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const checkStepRef = useRef<CheckStepHandle>(null)
  const [adherenceChecksByContainer, setAdherenceChecksByContainer] = useState<
    Record<string, AdherenceCheckUpsertPayload[]>
  >({})

  const handleClose = () => {
    setActiveStep(0)
    setMode('STEPPER')
    onClose()
  }

  const handleSave = () => {
    onSave({})
    handleClose()
  }

  const handleUploadComplete = () => {
    onSave({})
    setActiveStep(2)
    setMode('STEPPER')
  }

  const onCheckStepComplete = (bool: boolean) => {
    setContinueAllowed(bool)
  }

  // Callback to be passed to CheckStep, called when CheckStep's data is fully ready
  const handleCheckStepDataReady = useCallback(() => {
    setIsLoading(false)
  }, [])

  useEffect(() => {
    setSteps([
      { label: 'Add and review files' },
      { label: 'Exams and adherence criteria check' },
      { label: 'DICOM files PHI redaction' },
    ])

    setActiveStep(0)

    // Clean up the Uppy instance when the component unmounts
    return () => {
      resetUppy()
      setActiveStep(0)
    }
  }, [])

  const handleContinueClick = async () => {
    if (activeStep === 0) {
      setIsLoading(true)
      setActiveStep(1)
      // The loader will be stopped by CheckStep's onDataReady callback
      return
    }

    const commentsValid = (await checkStepRef.current?.validateAdherenceComments()) ?? true
    if (commentsValid) {
      setMode('UPLOAD')
    }
  }

  // Reset isLoading when activeStep changes (e.g., going back to AddFilesStep)
  useEffect(() => {
    if (activeStep !== 1) {
      setIsLoading(false)
    }
  }, [activeStep])

  return (
    <Dialog
      fullScreen
      open={true}
      onClose={handleClose}
      aria-labelledby="upload-dialog-title"
      aria-describedby="upload-dialog-description"
    >
      <AppBar
        position="static"
        sx={{
          bgcolor: theme.palette.background.default,
          boxShadow: 2,
          zIndex: 10,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Stack>
            <Typography id="upload-dialog-title" color="text.primary" variant="subtitle2">
              {timepointName}
            </Typography>
            <Typography id="upload-dialog-description" color="text.secondary" variant="body2">
              Subject {subjectPseudoId}
            </Typography>
          </Stack>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            size="large"
            aria-label="Save and close dialog"
          >
            Save and close
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent
        sx={{
          p: SPACING.noSpacing,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Grid2 container height={SIZING.minWidth}>
          <Grid2
            size={activeStep === 2 ? SIZING.gridSizeXxl : SIZING.gridSizeLg}
            offset={activeStep === 2 ? 0 : 2}
            pt={SPACING.spacingXxl}
          >
            {activeStep === 1 && isLoading && (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="50vh">
                <CircularProgress size={60} />
                <Typography color="text.secondary" variant="body1" mt={2}>
                  Processing files...
                </Typography>
              </Box>
            )}
            {activeStep === 0 && mode === 'STEPPER' && (
              <AddFilesStep
                uppy={uppy}
                containerParam={containerParam}
                onCheckStepComplete={onCheckStepComplete}
                data-testid="add-files-step"
              />
            )}
            {activeStep === 1 && mode === 'STEPPER' && (
              // Render CheckStep. It will decide whether to show its internal loader based on parentIsLoading.
              <CheckStep
                ref={checkStepRef}
                uppy={uppy}
                containerParam={containerParam}
                onCheckStepComplete={onCheckStepComplete}
                selectedSubmission={{ name: timepointName }}
                subjectPseudoId={subjectPseudoId}
                setActiveStep={setActiveStep}
                onDataReady={handleCheckStepDataReady}
                parentIsLoading={isLoading} // Pass the parent's loading state
                onAdherenceChecksReady={setAdherenceChecksByContainer}
                data-testid="check-step"
              />
            )}
            {activeStep === 2 && mode === 'STEPPER' && (
              <PHIRedactionStep studyId={studyId} subjectId={subjectId} timepointSubmissionId={timepointSubmissionId} />
            )}
            {mode === 'UPLOAD' && (
              <UploadStep
                uppy={uppy}
                onUploadCompleted={handleUploadComplete}
                adherenceChecksByContainer={adherenceChecksByContainer}
                data-testid="upload-step"
              />
            )}
          </Grid2>
        </Grid2>
      </DialogContent>
      {mode !== 'UPLOAD' && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            width: '100%',
            zIndex: 10,
            boxShadow:
              '0px -16px 16px -8px rgba(14, 63, 126, 0.03), 0px -8px 8px -4px rgba(14, 63, 126, 0.02), 0px -4px 4px -2px rgba(42, 51, 69, 0.02), 0px -2px 2px -1px rgba(42, 51, 69, 0.02), 0px -1px 1px -0.5px rgba(42, 51, 69, 0.02), 0px 0px 0px 1px rgba(14, 63, 126, 0.02)',
          }}
        >
          <AppBar
            position="static"
            sx={{
              bgcolor: theme.palette.background.default,
              boxShadow: 2,
            }}
          >
            <Toolbar sx={{ justifyContent: 'space-between', columnGap: 40 }}>
              <Box width={SIZING.dialogBoxWidth}>
                {activeStep !== 0 && (
                  <Button
                    size="large"
                    color="secondary"
                    variant="text"
                    startIcon={<ArrowBackRounded />}
                    onClick={() => setActiveStep(activeStep - 1)}
                    aria-label="Go to previous step"
                  >
                    Previous step
                  </Button>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <HorizontalLinearStepper
                  steps={steps}
                  activeStep={activeStep}
                  showStepLabels
                  aria-label="Upload process steps"
                />
              </Box>
              {activeStep < 2 && (
                <Button
                  size="large"
                  color="primary"
                  variant="contained"
                  onClick={handleContinueClick}
                  disabled={!continueAllowed || (activeStep === 1 && isLoading)} // Disable if CheckStep is loading (via parentIsLoading)
                >
                  Continue
                </Button>
              )}
            </Toolbar>
          </AppBar>
        </Box>
      )}
    </Dialog>
  )
}
